<template>
  <div v-if="cloud" class="lite_popup lite_business_cloud_popup" @click.stop>
    <div class="lite_popup_box lite_business_cloud_box" :class="{ 'lite_google_workspace_box': isGoogleWorkspace }"
      role="dialog" aria-modal="true" :aria-label="title">
      <div class="lite_popup_title">
        <span>{{ title }}</span>
        <button type="button" class="icon white-close lite_business_close" :disabled="!!connecting"
          :aria-label="$t('language.common.close')" @click="$emit('close')"></button>
      </div>
      <div class="lite_business_cloud_content" :class="{ 'lite_google_workspace_content': isGoogleWorkspace }">
        <template v-if="isGoogleWorkspace">
          <div class="lite_google_workspace_intro" v-html="$t('language.popup.addCloudPopup.googleWorkspaceGuideIntro')"></div>
          <div class="lite_google_workspace_step">
            <div v-if="guideStep === 1" v-html="$t('language.popup.addCloudPopup.googleWorkspaceGuideStep1')"></div>
            <template v-else-if="guideStep === 2">
              <div v-html="$t('language.popup.addCloudPopup.googleWorkspaceGuideStep2')"></div>
              <div class="lite_google_workspace_copy_rows">
                <div v-for="item in guideCopyItems" :key="item.value" class="lite_google_workspace_copy_row">
                  <span>{{ $t(item.label) }}:</span>
                  <code>{{ item.value }}</code>
                  <button type="button" class="lite_google_workspace_copy" :class="{ copied: copiedValue === item.value }"
                    :aria-label="$t('language.common.copy')" :title="copiedValue === item.value ? $t('language.liteApp.copySuccess') : $t('language.common.copy')"
                    @click="copyGuideValue(item.value)">
                    <i class="icon copy" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </template>
            <div v-else v-html="$t('language.popup.addCloudPopup.googleWorkspaceGuideStep3')"></div>
            <div class="lite_google_workspace_visual">
              <button v-if="guideStep > 1" type="button" class="lite_google_workspace_step_button previous"
                :aria-label="$t('language.common.upStep')" :title="$t('language.common.upStep')" @click="guideStep -= 1">
                <span aria-hidden="true"></span>
              </button>
              <button type="button" class="lite_google_workspace_image" :aria-label="$t('language.popup.addCloudPopup.googleWorkspaceGuideViewImage')"
                @click="previewImage = true">
                <img :src="guideImage" :alt="$t('language.popup.addCloudPopup.googleWorkspaceGuideImageAlt', { step: guideStep })">
              </button>
              <button v-if="guideStep < 3" type="button" class="lite_google_workspace_step_button next"
                :aria-label="$t('language.common.nextStep')" :title="$t('language.common.nextStep')" @click="guideStep += 1">
                <span aria-hidden="true"></span>
              </button>
            </div>
          </div>
          <div class="lite_google_workspace_personal" v-html="$t('language.popup.addCloudPopup.googleWorkspaceGuidePersonal')"></div>
        </template>
        <template v-if="cloud.description">
          <div v-html="$t('language.popup.addCloudPopup.' + cloud.description)"></div>
          <div class="lite_business_cloud_note" v-html="$t('language.popup.addCloudPopup.specialDropBoxBottom')"></div>
        </template>
        <div class="lite_business_cloud_actions">
          <button type="button" :disabled="!!connecting" @click="$emit('select', 'organization')">
            <template v-if="connecting === 'organization'">{{ $t('language.common.connecting') }}<span class="cloudDotting"></span></template>
            <template v-else>{{ $t('language.popup.addCloudPopup.accessOrganization') }}</template>
          </button>
          <button type="button" class="lite_business_personal" :disabled="!!connecting" @click="$emit('select', 'personal')">
            <template v-if="connecting === 'personal'">{{ $t('language.common.connecting') }}<span class="cloudDotting"></span></template>
            <template v-else>{{ $t('language.popup.addCloudPopup.accessPersonalCloud') }}</template>
          </button>
        </div>
      </div>
    </div>
    <div v-if="previewImage" class="lite_google_workspace_preview" role="dialog" aria-modal="true"
        :aria-label="$t('language.popup.addCloudPopup.googleWorkspaceGuideViewImage')" @click.self="previewImage = false">
      <div class="lite_google_workspace_preview_box">
        <button type="button" class="icon white-close lite_google_workspace_preview_close"
          :aria-label="$t('language.common.close')" @click="previewImage = false"></button>
        <img :src="guideImage" :alt="$t('language.popup.addCloudPopup.googleWorkspaceGuideImageAlt', { step: guideStep })">
      </div>
    </div>
  </div>
</template>

<script>
const CLOUDS = {
  GoogleWorkspace: { name: 'gSuite' },
  OneDriveBusiness: { name: 'oneDrive4Business', description: 'specialOnedrive' },
  DropboxBusiness: { name: 'dropbox4Business', description: 'specialDropBox' },
  BoxBusiness: { name: 'boxBusiness', description: 'specialBoxBusiness' },
};

export default {
  name: 'liteBusinessCloudPopup',
  props: {
    type: { type: String, default: '' },
    connecting: { type: String, default: '' },
  },
  data() {
    return {
      guideStep: 1,
      previewImage: false,
      copiedValue: '',
      copiedTimer: null,
    };
  },
  computed: {
    cloud() {
      return CLOUDS[this.type] || null;
    },
    title() {
      return this.cloud ? this.$t('language.common.add') + ' ' + this.$t('language.addCloud.' + this.cloud.name) : '';
    },
    isGoogleWorkspace() {
      return this.type === 'GoogleWorkspace';
    },
    guideImage() {
      return '/static/tips/AddGoogleWorkspace' + this.guideStep + '.png';
    },
    guideCopyItems() {
      return [
        { label: 'language.popup.addCloudPopup.googleWorkspaceGuideClientId', value: '108631162623464992162' },
        { label: 'language.popup.addCloudPopup.googleWorkspaceGuideScopes1', value: 'https://www.googleapis.com/auth/drive' },
        { label: 'language.popup.addCloudPopup.googleWorkspaceGuideScopes2', value: 'https://www.googleapis.com/auth/admin.directory.user.readonly' },
      ];
    },
  },
  watch: {
    type(value) {
      if (value === 'GoogleWorkspace') {
        this.guideStep = 1;
        this.previewImage = false;
        this.copiedValue = '';
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.copiedTimer);
  },
  methods: {
    copyGuideValue(value) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(value).then(() => this.setCopiedValue(value), () => this.copyGuideValueFallback(value));
        return;
      }
      this.copyGuideValueFallback(value);
    },
    copyGuideValueFallback(value) {
      const input = document.createElement('textarea');
      input.value = value;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(input);
      if (copied) this.setCopiedValue(value);
    },
    setCopiedValue(value) {
      this.copiedValue = value;
      this.$toast({
        text: this.$t('language.liteApp.copySuccess'),
        type: 'success',
        idValue: 'addClouds',
      });
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copiedValue = '';
      }, 2000);
    },
  },
};
</script>

<style scoped>
.lite_business_cloud_box {
  width: 635px;
  max-width: calc(100vw - 32px);
  min-height: 280px;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.lite_business_cloud_box.lite_google_workspace_box { width: 860px; }
.lite_business_cloud_box > .lite_popup_title {
  display: flex;
  align-items: flex-start;
  flex: 0 0 auto;
  height: auto;
  min-height: 40px;
  padding: 10px 15px 10px 20px;
  box-sizing: border-box;
  line-height: 20px;
}
.lite_business_cloud_box > .lite_popup_title > span {
  flex: 1;
  min-width: 0;
  margin-right: 12px;
  overflow-wrap: anywhere;
}
.lite_business_cloud_box .lite_business_close {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  padding: 0 !important;
  background-color: transparent;
}
.lite_business_cloud_content {
  min-height: 0;
  padding: 35px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 20px;
  overflow-wrap: anywhere;
  text-align: left;
}
.lite_business_cloud_note { margin-top: 20px; }
.lite_business_cloud_actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 40px;
  max-width: 480px;
  margin: 40px auto 0;
}
.lite_business_cloud_actions > button {
  flex: 1 1 200px;
  min-width: 0;
  min-height: 35px;
  height: auto;
  padding: 6px 10px !important;
  border: 1px solid #fff;
  border-radius: 5px;
  color: #fff;
  background: #5562b4;
  line-height: 21px;
  white-space: normal;
  overflow-wrap: anywhere;
}
.lite_business_cloud_actions > .lite_business_personal {
  color: #5562b4;
  border-color: #5562b4;
  background: #fff;
}
.lite_business_cloud_box button:disabled { cursor: default; opacity: .65; }
.lite_google_workspace_content {
  padding: 32px 48px 28px;
}
.lite_google_workspace_intro {
  margin-bottom: 20px;
  font-size: 16px;
  line-height: 22px;
}
.lite_google_workspace_step { line-height: 21px; }
.lite_google_workspace_copy_rows { margin-top: 12px; }
.lite_google_workspace_copy_row {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) 26px;
  gap: 6px;
  align-items: start;
  margin-top: 8px;
}
.lite_google_workspace_copy_row code {
  min-width: 0;
  color: inherit;
  font-family: inherit;
  overflow-wrap: anywhere;
}
.lite_google_workspace_copy {
  width: 22px;
  min-width: 22px;
  height: 22px;
  min-height: 22px;
  padding: 5px !important;
  border: 0;
  border-radius: 3px;
  background: transparent;
  line-height: 12px;
}
.lite_google_workspace_copy:hover,
.lite_google_workspace_copy:focus { background: #edf0ff; }
.lite_google_workspace_copy.copied { background: #e6f6eb; }
.lite_google_workspace_copy .copy { display: block; }
.lite_google_workspace_visual {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 22px 0;
}
.lite_google_workspace_image {
  display: block;
  flex: 0 1 650px;
  min-width: 0;
  min-height: 0;
  height: auto;
  padding: 0;
  border: 0;
  background: transparent;
  line-height: 0;
  cursor: zoom-in;
}
.lite_google_workspace_image img {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid #dcdcdc;
}
.lite_google_workspace_step_button {
  flex: 0 0 36px;
  width: 36px;
  min-width: 36px;
  height: 50px;
  min-height: 50px;
  padding: 0 !important;
  border: 0;
  background: transparent;
}
.lite_google_workspace_step_button span {
  display: block;
  width: 18px;
  height: 18px;
  margin: auto;
  border-top: 5px solid #333;
  border-right: 5px solid #333;
}
.lite_google_workspace_step_button.previous span { transform: rotate(-135deg); }
.lite_google_workspace_step_button.next span { transform: rotate(45deg); }
.lite_google_workspace_step_button:hover span,
.lite_google_workspace_step_button:focus span { border-color: #5562b4; }
.lite_google_workspace_personal {
  font-size: 16px;
  line-height: 22px;
}
.lite_google_workspace_preview {
  position: fixed;
  z-index: 1;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, .78);
}
.lite_google_workspace_preview_box {
  position: relative;
  display: flex;
  max-width: 100%;
  max-height: 100%;
}
.lite_google_workspace_preview_box img {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 64px);
  object-fit: contain;
}
.lite_google_workspace_preview_close {
  position: absolute;
  z-index: 1;
  top: -26px;
  right: -26px;
  width: 20px;
  height: 20px;
  padding: 0 !important;
  background-color: transparent;
}
@media (max-width: 600px) {
  .lite_business_cloud_content { padding: 24px 20px; }
  .lite_business_cloud_actions { margin-top: 28px; }
  .lite_google_workspace_content { padding: 24px 20px; }
  .lite_google_workspace_intro,
  .lite_google_workspace_personal { font-size: 14px; line-height: 20px; }
  .lite_google_workspace_visual { gap: 4px; margin: 16px 0; }
  .lite_google_workspace_step_button { flex-basis: 28px; width: 28px; min-width: 28px; }
  .lite_google_workspace_step_button span { width: 14px; height: 14px; border-width: 4px; }
  .lite_google_workspace_preview { padding: 20px; }
  .lite_google_workspace_preview_close { top: -24px; right: 0; }
}
</style>



// WEBPACK FOOTER //
// src/components/popup/add_cloud_popups/lite_business_cloud_popup.vue